import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { Tree } from 'primeng/tree';

import { DataSelectDialogServiceInterface } from '../../services/data-select-dialog.service.interface';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DataBrowserType } from '../../models/data-select-dialog.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

/**
 * Component that display a dialog to select from a list of data options.
 */
@Component({
    selector: 'agpr-data-select-dialog',
    templateUrl: './data-select-dialog.component.html',
    styleUrls: ['./data-select-dialog.component.scss'],
    standalone: false
})
export class DataSelectDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    // unique identifier from parent to id the dialog
    @Input() id: string;
    /** The icon to display for each list item. null is acceptable */
    @Input() listItemIcon: string;
    /** Toggles dialog visible or hidden*/
    @Input() visibleValue: boolean = false;
    /** Heading of the dialog */
    @Input() dialogHeader: string;
    /** Message to communicate to user */
    @Input() dialogMessage: string;
    /** Header of the left column */
    @Input() leftColumnHeader: string;
    /** Header of the right column */
    @Input() rightColumnHeader: string;
    /** Service used to get and return data */
    @Input() set dialogService(service: DataSelectDialogServiceInterface) {
        if (service) {
            this.dataSelectDialogService = service;
        }
    }
    /** Allows for the filter string to be set on init. Used when opened from some context, like autocomplete suggestions */
    @Input() set initialFilterString(query: string) {
        if (query) {
            this.filterString = query;
        }
    }
    @Input() lockMessage: string = 'Your account does not have access to this';
    /** Toggles the filter input for searching the available items */
    @Input() filterDataType: DataBrowserType = null;

    @Input() propagateSelection: boolean = true;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;

    @ViewChild('tree') tree: Tree;

    dataSelectDialogService: DataSelectDialogServiceInterface;

    availableItems: TreeNode[] = [];
    selectedItems: TreeNode[] = [];
    selectedItemsToDisplay: TreeNode[] = [];
    selectedItemTypesMap: Map<any, any>;
    selectedItemTypes = [];
    subscriptions: Subscription[] = [];

    filteredAvailableItems: TreeNode[] = [];
    selectedFilterItem: TreeNode<any>;
    filterString: string = '';

    readonly buttonTypes = ButtonTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    /**
    * Constructor for Component
    */
    constructor(
        public coreDialogService: CoreDialogService,
    ) {
        super(coreDialogService, 'DataSelect');
    }

    /**
    * Subscribes to datasource in OnInit lifecylce hook
    */
    ngOnInit() {
        if (this.dataSelectDialogService) {
            this.destroySubscriptions();
            this.createSubscriptions();

            if (this.filterString) {
                this.filterItems();
            }
        }
    }


    /**
    * Handles items being selected
    * @param event
    */
    itemSelected(event) {
        this.dataSelectDialogService.itemSelected(event.node);
    }


    itemUnselected(event) {
        this.dataSelectDialogService.itemRemoved(event.node, this.propagateSelection);
    }

    /**
    * Removes item from selected item list
    *
    * @param index position of item in selectedItems
    */
    removeItem(item: TreeNode) {
        this.dataSelectDialogService.displayItemDeleted(item, this.propagateSelection);
    }

    /**
    * Handler for closing dialog event
    */
    doHide() {
        this.selectedItems = [];
        this.closeDialog(this.dataSelectDialogService.getSelectedItems());
    }

    /**
     * Reset the selections
     */
    reset(){
        this.dataSelectDialogService.reset();
    }

    /**
    * Emits and event to confirm selected items
    */
    applySelected() {
        this.dataSelectDialogService.applySelectedListItems(this.selectedItems, this.propagateSelection);
        this.doHide();
    }

    /**
    * Handler for expanding a node
    */
    nodeExpanded(event) {
        this.dataSelectDialogService.nodeExpanded(event.node, this.propagateSelection);
    }

    /**
    * Handler for collapsing a node
    */
    nodeCollapsed(event) {
        this.dataSelectDialogService.nodeCollapsed(event.node);
    }

    selectionChange(event) {
        this.dataSelectDialogService.selectionChange(event, this.availableItems, this.propagateSelection);
    }

    filterItems() {
        if (this.filterString.length > 2) {
            this.dataSelectDialogService.filterItems(this.filterString)
                .pipe(take(1))
                .subscribe(filteredItems => {
                    this.filteredAvailableItems = filteredItems;
                });
        } else {
            this.filteredAvailableItems = this.resetAllItems(this.availableItems);
        }
    }

    private createSubscriptions(): void {
        this.subscriptions.push(
            this.dataSelectDialogService.getListItems()
                .pipe(filter(x => !!x))
                .subscribe(availableListItems => {
                    this.availableItems = availableListItems;
                    if (this.filterDataType) {
                        // Ensure all items start showing, and collapsed
                        // Sorting the children of the filteredAvailableItems
                        this.filteredAvailableItems = this.resetAllItems(this.availableItems).map(filterItemParent => {
                            filterItemParent.children?.sort((a, b) => {
                                if (a.label < b.label) {
                                    return -1;
                                }
                                if (a.label > b.label) {
                                    return 1;
                                }
                                return 0;
                            });
                            return filterItemParent;
                        });
                    }
                }),
            this.dataSelectDialogService.getSelectedListItems()
                .pipe(filter(x => !!x))
                .subscribe(selectedListItems => {
                    this.selectedItems = selectedListItems.slice();
                }),
            this.dataSelectDialogService.getSelectedItemsToDisplay()
                .pipe(filter(x => !!x))
                .subscribe(selectedDisplayItems => {
                    this.selectedItemsToDisplay = selectedDisplayItems.slice();
                }),
            this.dataSelectDialogService.getSelectedItemTypes()
                .pipe(filter(x => !!x))
                .subscribe(selectedItemTypesMap => {
                    this.selectedItemTypes = [];
                    this.selectedItemTypesMap = selectedItemTypesMap;
                    this.selectedItemTypes.push(...this.selectedItemTypesMap.keys());
                })
        );
    }

    private resetAllItems(items: TreeNode[]) {
        return items.map(item => {
            item.styleClass = '';
            item.expanded = false;
            item.children?.forEach(child => child.styleClass = '');
            return item;
        });
    }

    private destroySubscriptions() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    ngOnDestroy() {
        this.destroySubscriptions();
    }
}
